import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Region } from "../services/model";
import {
  useCreateRegionMutation,
  useCreateSiteMutation,
  useMoveRosterMutation,
  useMoveSiteMutation,
  useSitesAndRegionsTreeApiService,
} from "../services/api/SiteConfigurationApiService";

export interface SuccessAlertState {
  title: string;
  body: string;
}

export interface UseSitesAndRegionsResult {
  regions: Region[];
  isLoading: boolean;
  isError: boolean;
  createRegionOpen: boolean;
  setCreateRegionOpen: Dispatch<SetStateAction<boolean>>;
  newRegionName: string;
  setNewRegionName: Dispatch<SetStateAction<string>>;
  confirmCreateRegion: () => void;
  createSiteOpen: boolean;
  openCreateSite: (regionId?: string) => void;
  closeCreateSite: () => void;
  newSiteRegionId: string;
  setNewSiteRegionId: Dispatch<SetStateAction<string>>;
  newSiteName: string;
  setNewSiteName: Dispatch<SetStateAction<string>>;
  confirmCreateSite: () => void;
  pendingMoveSite: { siteId: string; fromRegionId: string } | null;
  setPendingMoveSite: Dispatch<
    SetStateAction<{ siteId: string; fromRegionId: string } | null>
  >;
  moveSiteDestination: string;
  setMoveSiteDestination: Dispatch<SetStateAction<string>>;
  confirmMoveSite: () => void;
  pendingMoveRoster: { rosterId: string; fromSiteId: string } | null;
  setPendingMoveRoster: Dispatch<
    SetStateAction<{ rosterId: string; fromSiteId: string } | null>
  >;
  moveRosterDestination: string;
  setMoveRosterDestination: Dispatch<SetStateAction<string>>;
  confirmMoveRoster: () => void;
  successAlert: SuccessAlertState | null;
  setSuccessAlert: Dispatch<SetStateAction<SuccessAlertState | null>>;
  allSites: Array<{ id: string; label: string }>;
  regionRosterCount: (region: Region) => number;
}

/**
 * Synthetic Region id used by the configuration tree for Sites without a Region.
 * It is NOT a real Region — Sites cannot be created into it.
 */
const UNGROUPED_REGION_ID = "ungrouped";

/**
 * Page-level state for the Sites & Regions overview.
 *
 * `regions` is served by the configuration-tree query; create/move actions call
 * the corresponding mutations and rely on query invalidation to refresh the
 * tree (no local tree mutation).
 */
export function useSitesAndRegions(): UseSitesAndRegionsResult {
  const treeQuery = useSitesAndRegionsTreeApiService();
  const regions = treeQuery.data ?? [];

  const createRegionMutation = useCreateRegionMutation();
  const createSiteMutation = useCreateSiteMutation();
  const moveSiteMutation = useMoveSiteMutation();
  const moveRosterMutation = useMoveRosterMutation();

  /* -------------- Create Region -------------- */
  const [createRegionOpen, setCreateRegionOpen] = useState(false);
  const [newRegionName, setNewRegionName] = useState("");

  /* -------------- Create Site -------------- */
  const [createSiteOpen, setCreateSiteOpen] = useState(false);
  const [newSiteRegionId, setNewSiteRegionId] = useState<string>("");
  const [newSiteName, setNewSiteName] = useState("");

  function openCreateSite(regionId?: string): void {
    setNewSiteRegionId(regionId ?? "");
    setNewSiteName("");
    setCreateSiteOpen(true);
  }

  function closeCreateSite(): void {
    setCreateSiteOpen(false);
    setNewSiteRegionId("");
    setNewSiteName("");
  }

  /* -------------- Move Site -------------- */
  const [pendingMoveSite, setPendingMoveSite] = useState<{
    siteId: string;
    fromRegionId: string;
  } | null>(null);
  const [moveSiteDestination, setMoveSiteDestination] = useState<string>("");

  /* -------------- Move Roster -------------- */
  const [pendingMoveRoster, setPendingMoveRoster] = useState<{
    rosterId: string;
    fromSiteId: string;
  } | null>(null);
  const [moveRosterDestination, setMoveRosterDestination] =
    useState<string>("");

  /* -------------- Success Alert -------------- */
  const [successAlert, setSuccessAlert] = useState<SuccessAlertState | null>(
    null
  );

  /* -------------- Derived: total roster count per region -------------- */
  const regionRosterCount = useCallback(
    (region: Region): number =>
      region.sites.reduce((acc, s) => acc + s.rosters.length, 0),
    []
  );

  /* -------------- Handlers -------------- */
  function confirmCreateRegion(): void {
    const name = newRegionName.trim();
    if (!name) return;
    createRegionMutation.mutate(
      { name },
      {
        onSuccess: () => {
          setSuccessAlert({ title: "Region created", body: `${name} added.` });
          setCreateRegionOpen(false);
          setNewRegionName("");
        },
      }
    );
  }

  function confirmCreateSite(): void {
    const regionId = newSiteRegionId;
    const name = newSiteName.trim();
    // The synthetic "ungrouped" bucket is not a real Region — Sites cannot be
    // created into it.
    if (!regionId || regionId === UNGROUPED_REGION_ID || !name) return;
    const regionName = regions.find((r) => r.id === regionId)?.name ?? "region";
    createSiteMutation.mutate(
      { name, site_group: regionId },
      {
        onSuccess: () => {
          setSuccessAlert({
            title: "Site created",
            body: `${name} added to ${regionName}.`,
          });
          closeCreateSite();
        },
      }
    );
  }

  function confirmMoveSite(): void {
    if (!pendingMoveSite || !moveSiteDestination) return;
    const { siteId, fromRegionId } = pendingMoveSite;
    const fromRegion = regions.find((r) => r.id === fromRegionId);
    const site = fromRegion?.sites.find((s) => s.id === siteId);
    const toRegion = regions.find((r) => r.id === moveSiteDestination);
    if (!site || !toRegion) return;
    moveSiteMutation.mutate(
      { siteId, site_group: moveSiteDestination },
      {
        onSuccess: () => {
          setSuccessAlert({
            title: "Site moved",
            body: `${site.name} moved to ${toRegion.name}.`,
          });
          setPendingMoveSite(null);
          setMoveSiteDestination("");
        },
      }
    );
  }

  function confirmMoveRoster(): void {
    if (!pendingMoveRoster || !moveRosterDestination) return;
    const { rosterId, fromSiteId } = pendingMoveRoster;
    const allRosters = regions.flatMap((r) =>
      r.sites.flatMap((s) => s.rosters)
    );
    const movedRoster = allRosters.find((rr) => rr.id === rosterId);
    const destSite = regions
      .flatMap((r) => r.sites)
      .find((s) => s.id === moveRosterDestination);
    if (!movedRoster || !destSite) return;
    moveRosterMutation.mutate(
      {
        rosterId,
        siteId: fromSiteId,
        data: { destination_site_id: moveRosterDestination },
      },
      {
        onSuccess: () => {
          setSuccessAlert({
            title: "Roster moved",
            body: `${movedRoster.name} moved to ${destSite.name}. All settings reset to the destination Site's defaults.`,
          });
          setPendingMoveRoster(null);
          setMoveRosterDestination("");
        },
      }
    );
  }

  // Flat list of all sites (across regions) for the Move Roster destination select.
  const allSites = useMemo(
    () =>
      regions.flatMap((r) =>
        r.sites.map((s) => ({
          id: s.id,
          label: `${s.name} (${r.name})`,
        }))
      ),
    [regions]
  );

  return {
    regions,
    isLoading: treeQuery.isLoading,
    isError: treeQuery.isError,
    createRegionOpen,
    setCreateRegionOpen,
    newRegionName,
    setNewRegionName,
    confirmCreateRegion,
    createSiteOpen,
    openCreateSite,
    closeCreateSite,
    newSiteRegionId,
    setNewSiteRegionId,
    newSiteName,
    setNewSiteName,
    confirmCreateSite,
    pendingMoveSite,
    setPendingMoveSite,
    moveSiteDestination,
    setMoveSiteDestination,
    confirmMoveSite,
    pendingMoveRoster,
    setPendingMoveRoster,
    moveRosterDestination,
    setMoveRosterDestination,
    confirmMoveRoster,
    successAlert,
    setSuccessAlert,
    allSites,
    regionRosterCount,
  };
}

export default useSitesAndRegions;
